









































import Vue, { PropOptions } from 'vue';
import validationRequired from '../../utils/validationRules/required';
import { ValidationRule } from '../../types';

/**
 * A wrapper around the vuetify text input that allows us to set some defaults, limit options and
 * maintain consistency.
 */
export default Vue.extend({
  name: 'SInputText',

  props: {
    /**
     * The value of the component
     */
    value: {
      type: [String, Number],
      required: true,
    },

    /**
     * Adds clear input functionality
     */
    clearable: {
      type: Boolean,
      default: false,
      required: false,
    },

    /**
     * Adds a counter for the input length
     */
    counter: {
      type: [Boolean, Number, String],
      default: false,
      required: false,
    },

    /**
     * Hint text
     */
    hint: {
      type: String,
      default: undefined,
      required: false,
    },

    /**
     * A persistent hint.
     */
    persistentHint: {
      type: Boolean,
      default: false,
      required: false,
    },

    /**
     * An icon that will be appended to the component
     */
    icon: {
      type: String,
      default: undefined,
      required: false,
    },

    /**
     * The input label
     */
    label: {
      type: String,
      default: undefined,
      required: false,
    },

    /**
     * The name attribute to apply to the input element
     */
    name: {
      type: String,
      default: undefined,
      required: false,
    },

    /**
     * The inputs placeholder text
     */
    placeholder: {
      type: String,
      default: undefined,
      required: false,
    },

    /**
     * If the input is required, uses the required validation rule from the utils
     */
    required: {
      type: Boolean,
      default: false,
      required: false,
    },

    /**
     * An array of validation rules to apply to the component.
     * See `ValidationRule` type in src/types.ts
     */
    rules: {
      type: Array,
      required: false,
      default: (): ValidationRule[] => [],
    } as PropOptions<ValidationRule[]>,

    /**
     * Set the type of the input
     */
    type: {
      type: String,
      default: 'text',
      required: false,
    },

    /**
     * Delays validation until blur event
     */
    validateOnBlur: {
      type: Boolean,
      default: false,
      required: false,
    },

    /**
     * Applies the filled input style
     */
    filled: {
      type: Boolean,
      required: false,
      default: true,
    },

    /**
     * Hides hint and validation errors
     */
    hideDetails: {
      type: Boolean,
      default: false,
    },

    /**
     * Disables the text input
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * Should the input be focused when mounted?
     */
    autofocus: {
      type: Boolean,
      default: false,
    },

    /**
     * The HTML autocomplete attribute
     */
    autocomplete: {
      type: String,
      required: false,
    },

    /**
     * The HTML 'step' attribute for number inputs
     */
    step: {
      type: String,
      required: false,
    },

    /**
     * Reduces the height of the input, only really makes a difference when `filled` is set to false
     */
    dense: {
      type: Boolean,
      default: false,
    },

    /**
     * Show a unit on the right of the text field
     */
    unit: {
      type: String,
      required: false,
    },

    /**
     * Turn the field into readonly mode
     */
    readonly: {
      type: Boolean,
      required: false,
    },
  },

  data(): any {
    return {
      /**
       * Vuetify adds `pointer: cursor` to the icon if there's a 'click:append' event defined, even
       * though most of the time all we do is emit an event. This only adds the event if the parent
       * is listening for it.
       * @see https://github.com/vuejs/vue/issues/7349#issuecomment-458405684
       */
      enableIconClick: this.$listeners['click:append'] ? 'click:append' : null,
    };
  },

  computed: {
    validationRules(): ValidationRule[] {
      const rules = [...this.rules];

      if (this.required) {
        rules.push(validationRequired);
      }

      return rules;
    },
  },

  beforeDestroy() {
    const emptyValue = typeof this.value === 'string' ? '' : 0;
    this.$emit('input', emptyValue);
  },
});
